import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  useLoadEmailMigrationQuery,
  useUpsertEmailMigrationMutation,
  useRefreshEmailMigrationPrimaryMutation,
} from '../app/api';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
};

const STATUS_COLORS = {
  'Not Started': '#9d9fa1',
  'In Progress': brand.tealBlue,
  'Complete':    '#2e7d32',
};

const TYPE_LABELS = {
  alias:   'Alias',
  mailbox: 'New Mailbox',
};

// Short labels for prior companies. Keys are normalized (lowercase,
// non-alphanumerics stripped) so spelling variants like "Oneteam Financial"
// and "oneteamfinancial" all collapse to the same short label.
const COMPANY_SHORT = {
  'oneteamfinancial':      'OTF',
  'hammerfinancial':       'Hammer',
  'hammerfinancialgroup':  'Hammer',
  'fscwealthadvisors':     'FSC',
};
const normalizeCompany = (c) => (c || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const shortCompany = (c) => {
  if (!c) return '';
  return COMPANY_SHORT[normalizeCompany(c)] || c;
};

// Employees from these firms get brand-new mailboxes; everyone else has
// their new address as an alias on their existing mailbox. Compared
// against the SHORT name so renames in COMPANY_SHORT stay in sync.
const MAILBOX_FIRMS = new Set(['OTF', 'Hammer', 'FSC']);

// IT staff who can be assigned a migration. Edit this list when the
// team changes — they appear in the per-row "Assigned To" dropdown.
const ASSIGNEES = ['John Corn', 'Joshua Dunaway', 'Jennifer Schell'];

// The "real" type for a row: an explicit override in migration_type wins,
// otherwise fall back to the prior_company rule above.
const effectiveType = (r) =>
  r.migration_type || (MAILBOX_FIRMS.has(shortCompany(r.prior_company)) ? 'mailbox' : 'alias');

// Per-type next-step hint shown beneath the row when expanded.
const TYPE_STEPS = {
  alias: [
    'Set prosperitycapitaladvisors.com address as the primary email in M365',
    'Update the UPN to the new address',
  ],
  mailbox: [
    'Confirm the user has logged in to the new mailbox',
    'Add the new address in Outlook',
    'Migrate emails from the prior tenant',
    'Migrate contacts from the prior tenant',
    'Migrate calendar from the prior tenant',
  ],
};

const sectionCard = (title, icon, children) => (
  <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', overflow: 'hidden', marginBottom: '1.5rem' }}>
    <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <i className={`bi bi-${icon}`} style={{ color: brand.goldenrod }} />
      <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>{title}</h5>
    </div>
    <div style={{ padding: '1.25rem' }}>{children}</div>
  </div>
);

export default function EmailMigration() {
  const { data: rows = [], isLoading, isError } = useLoadEmailMigrationQuery();
  const [upsert] = useUpsertEmailMigrationMutation();
  const [refreshPrimary, { isLoading: isRefreshing }] = useRefreshEmailMigrationPrimaryMutation();

  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [signInFilter, setSignInFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);

  // Per-directory M365 state from the Graph snapshot. Each entry has
  // primary_email, last_sign_in, last_password_change, account_enabled,
  // and matched. Emails are lowercased so we can compare case-insensitively.
  const [m365ByDir, setM365ByDir] = useState({});
  const [graphMeta, setGraphMeta] = useState(null); // { auto_completed, graph_user_count, fetched_at }
  const [graphError, setGraphError] = useState(null);

  const runRefreshPrimary = async () => {
    setGraphError(null);
    try {
      const res = await refreshPrimary().unwrap();
      setM365ByDir(res.by_directory || {});
      setGraphMeta({
        auto_completed: (res.auto_completed || []).length,
        graph_user_count: res.graph_user_count || 0,
        fetched_at: new Date(),
      });
    } catch (e) {
      setGraphError(e?.data?.detail || 'Microsoft 365 lookup failed.');
    }
  };

  // Fire one automatic refresh on first mount so the table loads with star
  // indicators without requiring an explicit click.
  const didAutoRefresh = useRef(false);
  useEffect(() => {
    if (didAutoRefresh.current) return;
    didAutoRefresh.current = true;
    runRefreshPrimary();
  }, []);

  const m365For = (directoryId) => m365ByDir[String(directoryId)] || null;
  const primaryFor = (directoryId) => (m365For(directoryId)?.primary_email || '').toLowerCase();

  // Short relative-date helper, e.g. "2d ago", "3w ago", "May 2".
  const formatActivity = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const ms = Date.now() - d.getTime();
    const day = 1000 * 60 * 60 * 24;
    const days = Math.floor(ms / day);
    if (days < 0) return d.toLocaleDateString();
    if (days === 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 14) return `${days}d ago`;
    if (days < 60) return `${Math.floor(days / 7)}w ago`;
    return d.toLocaleDateString();
  };

  // Local edits for the notes/assigned_to text fields so the user can type
  // without firing a request on every keystroke. Keyed by directory_id.
  const [drafts, setDrafts] = useState({});

  const draftOf = (r, field) => {
    const key = `${r.directory_id}:${field}`;
    if (key in drafts) return drafts[key];
    return r[field] ?? '';
  };
  const setDraft = (r, field, value) => {
    setDrafts(prev => ({ ...prev, [`${r.directory_id}:${field}`]: value }));
  };
  const clearDraft = (r, field) => {
    setDrafts(prev => {
      const next = { ...prev };
      delete next[`${r.directory_id}:${field}`];
      return next;
    });
  };

  const sendUpdate = async (r, overrides) => {
    const body = {
      directory_id: r.directory_id,
      migration_type: r.migration_type,
      status: r.status || 'Not Started',
      assigned_to: r.assigned_to,
      notes: r.notes,
      ...overrides,
    };
    await upsert(body);
  };

  const commitDraft = async (r, field) => {
    const key = `${r.directory_id}:${field}`;
    if (!(key in drafts)) return;
    const value = drafts[key];
    clearDraft(r, field);
    if (value === (r[field] ?? '')) return;
    await sendUpdate(r, { [field]: value || null });
  };

  // Distinct prior companies, keyed by short label so spelling variants
  // (e.g. "One Team Financial" vs "Oneteam Financial") collapse to one row.
  const companies = useMemo(() => {
    const set = new Set();
    rows.forEach(r => set.add(shortCompany(r.prior_company)));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  // Distinct assignees actually present in the data. Combined with the
  // canonical ASSIGNEES list so the dropdown always offers the current
  // team, but also surfaces any legacy free-text owner that's still set.
  const assigneeOptions = useMemo(() => {
    const set = new Set(ASSIGNEES);
    rows.forEach(r => { if (r.assigned_to) set.add(r.assigned_to); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(r => {
      if (typeFilter !== 'all' && effectiveType(r) !== typeFilter) return false;
      if (statusFilter !== 'all' && (r.status || 'Not Started') !== statusFilter) return false;
      if (companyFilter !== 'all' && shortCompany(r.prior_company) !== companyFilter) return false;
      if (assigneeFilter !== 'all') {
        if (assigneeFilter === '__unassigned__') {
          if (r.assigned_to) return false;
        } else if (r.assigned_to !== assigneeFilter) {
          return false;
        }
      }
      if (signInFilter !== 'all') {
        const m = m365ByDir[String(r.directory_id)] || null;
        if (signInFilter === 'never') {
          if (!m || !m.matched || m.last_sign_in) return false;
        } else if (signInFilter === 'has') {
          if (!m || !m.matched || !m.last_sign_in) return false;
        }
      }
      if (!q) return true;
      const hay = [
        r.first_name, r.last_name, r.final_email, r.prior_email,
        r.prior_company, shortCompany(r.prior_company), r.assigned_to, r.notes,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query, typeFilter, statusFilter, companyFilter, assigneeFilter, signInFilter, m365ByDir]);

  // Counts split by effective type so each path has its own progress view.
  const countsByType = useMemo(() => {
    const empty = () => ({ total: 0, 'Not Started': 0, 'In Progress': 0, 'Complete': 0 });
    const out = { alias: empty(), mailbox: empty() };
    rows.forEach(r => {
      const t = effectiveType(r);
      const s = r.status || 'Not Started';
      out[t].total += 1;
      out[t][s] = (out[t][s] || 0) + 1;
    });
    return out;
  }, [rows]);

  const pctOf = (c) => (c.total ? Math.round((c['Complete'] / c.total) * 100) : 0);

  const inputStyle = {
    width: '100%', padding: '6px 10px', border: `1px solid ${brand.softGray}`,
    borderRadius: '6px', fontSize: '0.85rem', color: brand.deepBlue, outline: 'none', fontFamily: 'inherit', background: 'white',
  };

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>

      {/* Header banner */}
      <div style={{ background: brand.deepBlue, borderRadius: '12px', padding: '1.75rem 2rem', marginBottom: '1.5rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, right: 0, width: '300px', height: '100%', background: 'linear-gradient(135deg, transparent 40%, rgba(0,105,166,0.3))', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: brand.goldenrod }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ background: 'rgba(208,155,44,0.15)', border: `2px solid ${brand.goldenrod}`, borderRadius: '12px', padding: '0.75rem', flexShrink: 0 }}>
            <i className="bi bi-envelope-paper" style={{ color: brand.goldenrod, fontSize: '1.75rem' }} />
          </div>
          <div>
            <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 4px' }}>
              Tech
            </p>
            <h3 style={{ color: 'white', fontWeight: 800, margin: '0 0 4px' }}>Email Domain Migration</h3>
            <p style={{ color: 'rgba(255,255,255,0.65)', margin: 0, fontSize: '0.875rem' }}>
              Track each employee&rsquo;s move to <strong>prosperitycapitaladvisors.com</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* Status summary — one panel per migration path. */}
      <div className="row g-3 mb-4">
        {['alias', 'mailbox'].map(t => {
          const c = countsByType[t];
          const p = pctOf(c);
          return (
            <div key={t} className="col-12 col-lg-6">
              <div style={{ background: 'white', borderRadius: '12px', padding: '1.1rem 1.25rem', boxShadow: '0 2px 8px rgba(10,41,71,0.08)', borderLeft: `4px solid ${brand.goldenrod}` }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                  <div>
                    <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '1px', color: brand.charcoal, fontWeight: 700 }}>
                      {TYPE_LABELS[t]}
                    </div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: brand.deepBlue }}>
                      {c.total} {c.total === 1 ? 'person' : 'people'}
                    </div>
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color: brand.deepBlue }}>{p}%</div>
                </div>
                <div style={{ height: 8, background: brand.softGray, borderRadius: 4, overflow: 'hidden', marginBottom: '0.75rem' }}>
                  <div style={{ height: '100%', width: `${p}%`, background: STATUS_COLORS['Complete'] }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
                  {['Not Started', 'In Progress', 'Complete'].map(s => (
                    <div key={s} style={{ borderTop: `3px solid ${STATUS_COLORS[s]}`, padding: '0.4rem 0.5rem', background: '#fafaf7', borderRadius: 6 }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: 800, color: brand.deepBlue, lineHeight: 1 }}>{c[s] || 0}</div>
                      <div style={{ fontSize: '0.7rem', color: brand.charcoal, fontWeight: 600, marginTop: 2 }}>{s}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {sectionCard('People', 'people-fill',
        <>
          {/* Filters */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <input
              style={{ ...inputStyle, maxWidth: 300 }}
              placeholder="Search name, email, owner…"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
            <select style={{ ...inputStyle, maxWidth: 180 }} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
              <option value="all">All types</option>
              <option value="alias">Alias</option>
              <option value="mailbox">New Mailbox</option>
            </select>
            <select style={{ ...inputStyle, maxWidth: 180 }} value={companyFilter} onChange={e => setCompanyFilter(e.target.value)}>
              <option value="all">All companies</option>
              {companies.map(c => (
                <option key={c} value={c}>{c || '— None —'}</option>
              ))}
            </select>
            <select style={{ ...inputStyle, maxWidth: 180 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="all">All statuses</option>
              <option value="Not Started">Not Started</option>
              <option value="In Progress">In Progress</option>
              <option value="Complete">Complete</option>
            </select>
            <select style={{ ...inputStyle, maxWidth: 180 }} value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)}>
              <option value="all">All assignees</option>
              <option value="__unassigned__">Unassigned</option>
              {assigneeOptions.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            <select style={{ ...inputStyle, maxWidth: 180 }} value={signInFilter} onChange={e => setSignInFilter(e.target.value)}>
              <option value="all">All sign-in</option>
              <option value="never">Never signed in</option>
              <option value="has">Has signed in</option>
            </select>
            <span style={{ marginLeft: 'auto', alignSelf: 'center', color: brand.charcoal, fontSize: '0.85rem' }}>
              {filtered.length} of {rows.length}
            </span>
            <button
              onClick={runRefreshPrimary}
              disabled={isRefreshing}
              style={{
                background: brand.deepBlue, color: 'white', border: 'none', borderRadius: 6,
                padding: '6px 12px', fontWeight: 600, fontSize: '0.8rem', cursor: isRefreshing ? 'wait' : 'pointer',
                opacity: isRefreshing ? 0.7 : 1,
              }}
              title="Re-query Microsoft 365 for each user's current primary email"
            >
              <i className={`bi ${isRefreshing ? 'bi-arrow-clockwise' : 'bi-microsoft'} me-1`} />
              {isRefreshing ? 'Checking M365…' : 'Refresh from M365'}
            </button>
          </div>

          {(graphMeta || graphError) && (
            <div style={{ marginBottom: '0.75rem', fontSize: '0.78rem', color: graphError ? '#b00020' : brand.charcoal }}>
              {graphError ? (
                <><i className="bi bi-exclamation-triangle me-1" />{graphError}</>
              ) : (
                <>
                  <i className="bi bi-star-fill me-1" style={{ color: brand.goldenrod }} />
                  Primary email shown with a star.
                  {' '}Last checked {graphMeta.fetched_at.toLocaleTimeString()}
                  {' '}({graphMeta.graph_user_count} M365 users
                  {graphMeta.auto_completed ? `, auto-completed ${graphMeta.auto_completed}` : ''}).
                </>
              )}
            </div>
          )}

          {isLoading ? (
            <div style={{ padding: '3rem', textAlign: 'center' }}>
              <div className="spinner-border" style={{ color: brand.tealBlue }} />
            </div>
          ) : isError ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: brand.charcoal }}>
              <i className="bi bi-exclamation-triangle" style={{ fontSize: '1.5rem', color: brand.goldenrod, display: 'block', marginBottom: '0.5rem' }} />
              Unable to load migration status.
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: brand.charcoal }}>
              <i className="bi bi-inbox" style={{ fontSize: '2.5rem', opacity: 0.4, display: 'block', marginBottom: '0.5rem' }} />
              No employees match the current filters.
            </div>
          ) : (
            <div className="table-responsive">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${brand.softGray}` }}>
                    {['Name', 'Email', 'Type', 'Status', 'Assigned To', ''].map(h => (
                      <th key={h} style={{ padding: '0.5rem 0.6rem', color: brand.charcoal, fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'left' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => {
                    const isOpen = expanded === r.directory_id;
                    const status = r.status || 'Not Started';
                    const type = effectiveType(r);
                    return (
                      <React.Fragment key={r.directory_id}>
                        <tr style={{ borderBottom: `1px solid ${brand.softGray}` }}>
                          <td style={{ padding: '0.6rem', fontWeight: 600, color: brand.deepBlue }}>
                            {r.first_name} {r.last_name}
                            {r.prior_company && (
                              <div style={{ fontSize: '0.7rem', color: brand.charcoal, fontWeight: 400 }}>{shortCompany(r.prior_company)}</div>
                            )}
                          </td>
                          <td style={{ padding: '0.6rem', color: brand.charcoal }}>
                            {(() => {
                              const primary = primaryFor(r.directory_id);
                              const finalIsPrimary = primary && r.final_email && primary === r.final_email.toLowerCase();
                              const priorIsPrimary = primary && r.prior_email && primary === r.prior_email.toLowerCase();
                              const m = m365For(r.directory_id);
                              const Star = () => (
                                <i
                                  className="bi bi-star-fill"
                                  title="Current primary in Microsoft 365"
                                  style={{ color: brand.goldenrod, marginRight: 4 }}
                                />
                              );
                              let activity = null;
                              if (m && m.matched) {
                                if (m.account_enabled === false) {
                                  activity = (
                                    <span style={{ color: '#b00020' }}>
                                      <i className="bi bi-slash-circle me-1" />Account disabled
                                    </span>
                                  );
                                } else if (!m.last_sign_in) {
                                  activity = (
                                    <span style={{ color: '#b00020' }}>
                                      <i className="bi bi-exclamation-triangle me-1" />Never signed in
                                    </span>
                                  );
                                } else {
                                  activity = (
                                    <span style={{ color: '#2e7d32' }} title={new Date(m.last_sign_in).toLocaleString()}>
                                      <i className="bi bi-check-circle me-1" />Signed in {formatActivity(m.last_sign_in)}
                                    </span>
                                  );
                                }
                              }
                              return (
                                <>
                                  <div style={{ color: brand.deepBlue, fontWeight: 600 }}>
                                    {finalIsPrimary && <Star />}
                                    {r.final_email}
                                  </div>
                                  <div style={{
                                    fontSize: '0.75rem', color: brand.charcoal,
                                    textDecoration: r.prior_email && !priorIsPrimary ? 'line-through' : 'none',
                                    opacity: r.prior_email && !priorIsPrimary ? 0.75 : 1,
                                  }}>
                                    {priorIsPrimary && <Star />}
                                    {r.prior_email || '—'}
                                  </div>
                                  {activity && (
                                    <div style={{ marginTop: 4, fontSize: '0.7rem' }}>{activity}</div>
                                  )}
                                </>
                              );
                            })()}
                          </td>
                          <td style={{ padding: '0.4rem' }}>
                            <select
                              style={{ ...inputStyle, minWidth: 130 }}
                              value={type}
                              onChange={e => sendUpdate(r, { migration_type: e.target.value })}
                            >
                              <option value="alias">Alias</option>
                              <option value="mailbox">New Mailbox</option>
                            </select>
                          </td>
                          <td style={{ padding: '0.4rem' }}>
                            <select
                              value={status}
                              onChange={e => sendUpdate(r, { status: e.target.value })}
                              style={{
                                background: 'transparent', border: 'none', fontWeight: 700,
                                color: STATUS_COLORS[status], fontSize: '0.85rem', cursor: 'pointer', outline: 'none',
                              }}
                            >
                              {Object.keys(STATUS_COLORS).map(s => <option key={s}>{s}</option>)}
                            </select>
                          </td>
                          <td style={{ padding: '0.4rem' }}>
                            <select
                              style={{ ...inputStyle, minWidth: 150 }}
                              value={r.assigned_to || ''}
                              onChange={e => sendUpdate(r, { assigned_to: e.target.value || null })}
                            >
                              <option value="">— Unassigned —</option>
                              {ASSIGNEES.map(name => (
                                <option key={name} value={name}>{name}</option>
                              ))}
                              {/* Preserve any legacy free-text value so it's still visible/editable. */}
                              {r.assigned_to && !ASSIGNEES.includes(r.assigned_to) && (
                                <option value={r.assigned_to}>{r.assigned_to}</option>
                              )}
                            </select>
                          </td>
                          <td style={{ padding: '0.6rem', textAlign: 'right' }}>
                            <button
                              onClick={() => setExpanded(isOpen ? null : r.directory_id)}
                              style={{ background: 'transparent', border: `1px solid ${brand.softGray}`, borderRadius: 6, padding: '4px 8px', color: brand.tealBlue, cursor: 'pointer', fontSize: '0.75rem' }}
                              title={isOpen ? 'Hide notes' : 'Show notes & steps'}
                            >
                              <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'}`} />
                            </button>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr style={{ background: '#fafaf7', borderBottom: `1px solid ${brand.softGray}` }}>
                            <td colSpan={6} style={{ padding: '0.75rem 1rem' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                                <div>
                                  <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: brand.charcoal, fontWeight: 700, marginBottom: 4 }}>
                                    Notes
                                  </div>
                                  <textarea
                                    style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
                                    placeholder="Blockers, special handling, when scheduled…"
                                    value={draftOf(r, 'notes')}
                                    onChange={e => setDraft(r, 'notes', e.target.value)}
                                    onBlur={() => commitDraft(r, 'notes')}
                                  />
                                  {r.completed_at && (
                                    <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#2e7d32' }}>
                                      <i className="bi bi-check-circle me-1" />
                                      Completed {new Date(r.completed_at).toLocaleDateString()}
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: brand.charcoal, fontWeight: 700, marginBottom: 4 }}>
                                    {TYPE_LABELS[type]} steps
                                  </div>
                                  <ol style={{ margin: 0, paddingLeft: '1.1rem', color: brand.charcoal, fontSize: '0.8rem' }}>
                                    {TYPE_STEPS[type].map((step, i) => (
                                      <li key={i} style={{ marginBottom: 4 }}>{step}</li>
                                    ))}
                                  </ol>
                                </div>
                                <div>
                                  <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: brand.charcoal, fontWeight: 700, marginBottom: 4 }}>
                                    M365 Account
                                  </div>
                                  {(() => {
                                    const m = m365For(r.directory_id);
                                    if (!m) {
                                      return <div style={{ color: brand.charcoal, fontSize: '0.8rem', fontStyle: 'italic' }}>Refresh from M365 to check account state.</div>;
                                    }
                                    if (!m.matched) {
                                      return <div style={{ color: '#b00020', fontSize: '0.8rem' }}><i className="bi bi-question-circle me-1" />No matching M365 user found.</div>;
                                    }
                                    const rowStyle = (tone) => ({ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', padding: '2px 0', color: tone || brand.charcoal });
                                    const accountTone = m.account_enabled === false ? '#b00020' : '#2e7d32';
                                    const lastSignInTone = m.last_sign_in ? undefined : '#b00020';
                                    return (
                                      <div>
                                        <div style={rowStyle(accountTone)}>
                                          <span>Account</span>
                                          <span style={{ fontWeight: 600 }}>{m.account_enabled === false ? 'Disabled' : 'Enabled'}</span>
                                        </div>
                                        <div style={rowStyle(lastSignInTone)}>
                                          <span>Last sign-in</span>
                                          <span style={{ fontWeight: 600 }}>{m.last_sign_in ? new Date(m.last_sign_in).toLocaleString() : 'Never'}</span>
                                        </div>
                                        <div style={rowStyle()}>
                                          <span>Password set</span>
                                          <span style={{ fontWeight: 600 }}>{m.last_password_change ? new Date(m.last_password_change).toLocaleDateString() : 'Never'}</span>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
